import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { Database, CheckCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { DataService } from '../../services/dataService';
import { AUDIO_EXT_REGEX } from '../audio/audioFormats';
import InlineAudioCell from '../audio/InlineAudioCell';
import MediaCell, { detectMediaColumnPairs } from './MediaCell';
import './DataPreview.css'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Tauri API loader (matches DataLoader / AudioAudit pattern) ─────────────
let _tauriApiCache = null;
const getTauriApi = () => {
  if (!(typeof window !== 'undefined' && window.__TAURI_INTERNALS__)) return null;
  if (_tauriApiCache) return _tauriApiCache;
  return (async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      _tauriApiCache = { invoke };
      return _tauriApiCache;
    } catch (e) {
      return null;
    }
  })();
};

const DataPreview = ({ primaryData, backcheckData, projectId, onNavigate }) => {
  
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeDataset, setActiveDataset] = useState('primary');
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const { isDarkMode } = useTheme();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pageData, setPageData] = useState(null);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Whether we're using server-side pagination (projectId available)
  const useServerPagination = !!projectId;

  // ── Media support: fetch project's media_dir and resolve Tauri API ───────
  // mediaDir is the project-wide local folder for ALL media types — images,
  // audio, video, PDF. When set + we're in Tauri, MediaCell can render
  // bare-filename cells (e.g. SurveyCTO desktop exports where the data has
  // "boat-1_24_31.JPG" with the actual file living on disk). When unset
  // or web, bare filenames render as plain text.
  //
  // Reads from the new media_settings endpoint, which transparently falls
  // back to the legacy audio_settings storage for projects that pre-date
  // the rename.
  const [mediaDir, setMediaDir] = useState(null);
  const [tauriApi, setTauriApi] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const api = await getTauriApi();
      if (mounted) setTauriApi(api);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await DataService.getMediaSettings(projectId);
        if (!cancelled) setMediaDir(settings?.media_dir || null);
      } catch (e) {
        // 404 or anything else — no media settings available, that's fine
        if (!cancelled) setMediaDir(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Colors matching DataSummary.js
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#1A1A1D' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    headerBg: isDarkMode ? '#0D0E0E' : '#F9FAFB',
    rowHover: isDarkMode ? '#1A1A1D' : '#F9FAFB',
    rowAlt: isDarkMode ? '#1f1f24ff' : '#FAFAFA',
    accent: '#2B8AFF',
    success: '#3aa092ff',
    warning: '#F59E0B',
    error: '#EF4444',
  };
  
  // =========================================================================
  // Server-side pagination: fetch data from /api/projects/{id}/data
  // =========================================================================
  const fetchPage = useCallback(async () => {
    if (!useServerPagination || !projectId) return;
    
    setIsLoadingPage(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        file_type: activeDataset,
      });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (sortConfig.column) {
        params.append('sort_by', sortConfig.column);
        params.append('sort_order', sortConfig.direction);
      }
      
      const token = localStorage.getItem('token');
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const headers = { 'Accept': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (userInfo?.email) headers['user_email'] = userInfo.email;
      
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/data?${params}`,
        { headers }
      );
      
      if (response.ok) {
        const result = await response.json();
        setPageData(result);
      } else {
        console.error('Failed to fetch page data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching page data:', error);
    } finally {
      setIsLoadingPage(false);
    }
  }, [projectId, currentPage, pageSize, activeDataset, debouncedSearch, sortConfig, useServerPagination]);
  
  // Fetch when pagination params change
  useEffect(() => {
    if (useServerPagination) {
      fetchPage();
    }
  }, [fetchPage, useServerPagination]);
  
  // Reset page when dataset, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeDataset, debouncedSearch, sortConfig]);
  
  // =========================================================================
  // Client-side fallback (no projectId — use data from props, still paginated)
  // =========================================================================
  const currentPropsData = activeDataset === 'primary' ? primaryData : backcheckData;
  
  const clientData = useMemo(() => {
    if (useServerPagination) return [];
    if (!currentPropsData?.data) return [];
    
    let result = [...currentPropsData.data];
    
    // Client-side search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(row =>
        Object.values(row).some(v => v != null && String(v).toLowerCase().includes(q))
      );
    }
    
    // Client-side sort
    if (sortConfig.column) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.column];
        const bVal = b[sortConfig.column];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    
    return result;
  }, [currentPropsData, debouncedSearch, sortConfig, useServerPagination]);
  
  const clientTotalPages = Math.max(1, Math.ceil(clientData.length / pageSize));
  const clientPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return clientData.slice(start, start + pageSize);
  }, [clientData, currentPage, pageSize]);
  
  // =========================================================================
  // Unified display data (server vs client)
  // =========================================================================
  const displayRows = useServerPagination ? (pageData?.data || []) : clientPageData;
  const totalRows = useServerPagination 
    ? (pageData?.total_rows ?? currentPropsData?.total_rows ?? 0) 
    : clientData.length;
  const totalPages = useServerPagination 
    ? (pageData?.total_pages ?? 1) 
    : clientTotalPages;
  const columns = useMemo(() => {
    if (useServerPagination && pageData?.columns?.length) return pageData.columns;
    if (displayRows.length > 0) return Object.keys(displayRows[0]);
    if (currentPropsData?.columns?.length) return currentPropsData.columns;
    return [];
  }, [useServerPagination, pageData, displayRows, currentPropsData]);

  // Detect audio columns by sampling cell contents — same heuristic as
  // AudioAudit so the two surfaces always agree on what's audio.
  const audioColumns = useMemo(() => {
    if (!columns.length || !displayRows.length) return new Set();
    const sample = displayRows.slice(0, 50);
    const result = new Set();
    for (const col of columns) {
      let nonEmpty = 0, audioLike = 0;
      for (const row of sample) {
        const v = row && typeof row === 'object' ? row[col] : null;
        if (v != null && String(v).trim()) {
          nonEmpty++;
          if (AUDIO_EXT_REGEX.test(String(v))) audioLike++;
        }
      }
      if (nonEmpty > 0 && audioLike / nonEmpty >= 0.5) result.add(col);
    }
    return result;
  }, [columns, displayRows]);

  // Whether inline audio rendering is available right now (Tauri + media_dir + audio cols)
  const inlineAudioReady = !!(tauriApi && mediaDir && audioColumns.size > 0);

  // Detect non-URL media columns by sampling cell values. Drives the
  // "set media folder" banner: if data has bare-filename media cells
  // (e.g. "boat-1_24_31.JPG", not a URL) and no folder is set, the user
  // can't see the files — surface a prompt.
  //
  // Heuristic: column has at least one non-empty, non-URL cell ending
  // in a recognized media extension. Sample 50 rows for parity with
  // audioColumns above.
  const localMediaColumns = useMemo(() => {
    if (!columns.length || !displayRows.length) return new Set();
    const MEDIA_EXT_REGEX = /\.(jpg|jpeg|png|gif|webp|bmp|svg|mp3|m4a|wav|ogg|aac|mp4|mov|avi|webm|mkv|pdf)$/i;
    const URL_REGEX = /^https?:\/\//i;
    const sample = displayRows.slice(0, 50);
    const result = new Set();
    for (const col of columns) {
      for (const row of sample) {
        const v = row && typeof row === 'object' ? row[col] : null;
        if (v == null) continue;
        const s = String(v).trim();
        if (!s || URL_REGEX.test(s)) continue;
        if (MEDIA_EXT_REGEX.test(s)) {
          result.add(col);
          break;
        }
      }
    }
    return result;
  }, [columns, displayRows]);

  // Banner visibility: Tauri + folder unset + at least one local media
  // column detected + not dismissed this session. Web users can't pick
  // local folders so the banner is hidden for them — they see the URL
  // proxy path instead, which works without a media folder.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [pickingFolder, setPickingFolder] = useState(false);
  const showMediaFolderBanner =
    !!tauriApi && !mediaDir && !bannerDismissed && localMediaColumns.size > 0;

  // Folder picker — same Tauri command Audio Audit uses; writes to the
  // project-wide media_settings (with audio_settings fallback handled
  // by the backend read path).
  const handlePickMediaFolder = useCallback(async () => {
    if (!tauriApi?.invoke || !projectId) return;
    setPickingFolder(true);
    try {
      const path = await tauriApi.invoke('pick_folder', {
        title: 'Choose your project media folder',
      });
      if (!path) {
        setPickingFolder(false);
        return;
      }
      setMediaDir(path); // optimistic
      try {
        await DataService.saveMediaSettings(projectId, { media_dir: path });
      } catch (e) {
        console.warn('Could not persist media settings to backend:', e);
      }
    } catch (e) {
      console.error('Folder picker failed:', e);
    } finally {
      setPickingFolder(false);
    }
  }, [tauriApi, projectId]);

  // Detect filename↔URL pairs from column names (KoBo / SCTO export convention).
  // For each pair { evidence: 'evidence_URL' }, the filename column gets a
  // MediaCell preview button using the URL from the paired column. The URL
  // column itself still renders, but as an external link (KoBo URLs lack
  // file extensions so they fall through to the link branch automatically).
  const urlPairs = useMemo(() => detectMediaColumnPairs(columns), [columns]);

  const totalVariables = useServerPagination 
    ? (pageData?.column_types ? Object.keys(pageData.column_types).length : columns.length)
    : (currentPropsData?.total_variables || columns.length);
  
  // =========================================================================
  // Guards
  // =========================================================================
  if (!primaryData && !backcheckData) return null;
  
  if (!currentPropsData && activeDataset === 'primary' && backcheckData) {
    setTimeout(() => setActiveDataset('backcheck'), 0);
    return null;
  } else if (!currentPropsData && activeDataset === 'backcheck' && primaryData) {
    setTimeout(() => setActiveDataset('primary'), 0);
    return null;
  }
  
  if (!useServerPagination && (!currentPropsData || !currentPropsData.data || currentPropsData.data.length === 0)) {
    return null;
  }
  
  // =========================================================================
  // Handlers
  // =========================================================================
  const handleSort = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        if (prev.direction === 'asc') return { column, direction: 'desc' };
        return { column: null, direction: 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };
  
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };
  
  const safePage = Math.min(currentPage, totalPages);
  const startRow = totalRows > 0 ? (safePage - 1) * pageSize + 1 : 0;
  const endRow = Math.min(safePage * pageSize, totalRows);
  
  // =========================================================================
  // Sort indicator
  // =========================================================================
  const SortIndicator = ({ column }) => {
    const isActive = sortConfig.column === column;
    const direction = sortConfig.direction;
    return (
      <span style={{
        display: 'inline-flex',
        flexDirection: 'column',
        marginLeft: '6px',
        opacity: isActive ? 1 : 0.3,
        transition: 'opacity 0.2s ease',
      }}>
        <svg width="8" height="6" viewBox="0 0 8 6" 
          fill={isActive && direction === 'asc' ? '#0EA5E9' : (isDarkMode ? '#6B7280' : '#9CA3AF')}
          style={{ marginBottom: '-2px' }}>
          <path d="M4 0L8 6H0L4 0Z" />
        </svg>
        <svg width="8" height="6" viewBox="0 0 8 6" 
          fill={isActive && direction === 'desc' ? '#0EA5E9' : (isDarkMode ? '#6B7280' : '#9CA3AF')}
          style={{ marginTop: '-2px' }}>
          <path d="M4 6L0 0H8L4 6Z" />
        </svg>
      </span>
    );
  };
  
  
  // Dataset toggle
  
  const DatasetToggle = () => (
    <div className="ds-tabs-container">
      <button
        onClick={() => {
          setActiveDataset('primary');
          setSortConfig({ column: null, direction: 'asc' });
          setSearchQuery('');
        }}
        disabled={!primaryData}
        className={`ds-tab ${activeDataset === 'primary' ? 'active' : ''}`}
        style={{ color: isDarkMode ? '#E5E7EB' : '#111827' }}
      >
        <Database size={13} />
        <span>Primary Survey</span>
        {primaryData && (
          <span className="ds-tab-count">
            {primaryData.total_rows?.toLocaleString()} rows
          </span>
        )}
      </button>
      <button
        onClick={() => {
          setActiveDataset('backcheck');
          setSortConfig({ column: null, direction: 'asc' });
          setSearchQuery('');
        }}
        disabled={!backcheckData}
        className={`ds-tab ${activeDataset === 'backcheck' ? 'active' : ''}`}
        style={{ color: isDarkMode ? '#E5E7EB' : '#111827' }}
      >
        <CheckCircle size={13} />
        <span>Back-Check</span>
        {backcheckData && (
          <span className="ds-tab-count">
            {backcheckData.total_rows?.toLocaleString()} rows
          </span>
        )}
      </button>
    </div>
  );

  
  // Render
  
  return (
    <div style={{ marginBottom: '0px' }}>
      {/* Header */}
      <div style={{
        padding: isMobile ? '16px' : '20px 24px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            fontSize: isMobile ? '16px' : '22px',
            fontWeight: '500',
            letterSpacing: '-0.01em',
            color: isDarkMode ? '#F1F5F9' : '#1E293B',
            marginBottom: '6px',
          }}>
            Data Preview
          </h1>
          <p style={{
            fontSize: '14px',
            color: colors.textSecondary,
            margin: 0
          }}>
            {totalRows.toLocaleString()} observations × {totalVariables.toLocaleString()} variables
          </p>
          <div style={{ marginTop: '16px' }}>
            <DatasetToggle />
          </div>
        </div>
      </div>

      {/* Media folder banner — prompts user to set the project media folder
          when local-filename media columns are detected and no folder is set.
          Only shown in DataQuad Desktop (Tauri); web users can't pick local
          folders so it's hidden for them. Dismissible per session. */}
      {showMediaFolderBanner && (
        <div style={{
          margin: '12px 16px 0',
          padding: '12px 14px',
          backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.08)' : '#EFF6FF',
          border: `1px solid ${isDarkMode ? 'rgba(37, 99, 235, 0.25)' : '#BFDBFE'}`,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '13px',
          color: isDarkMode ? '#DBEAFE' : '#1E3A8A',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.8 }}>
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: '2px' }}>
              {localMediaColumns.size === 1
                ? '1 media column detected'
                : `${localMediaColumns.size} media columns detected`}
            </div>
            <div style={{ opacity: 0.85, fontSize: '12px' }}>
              Set your project media folder to preview attached images, audio, video, and PDF files.
            </div>
          </div>
          <button
            onClick={handlePickMediaFolder}
            disabled={pickingFolder}
            style={{
              padding: '6px 12px',
              backgroundColor: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: pickingFolder ? 'wait' : 'pointer',
              opacity: pickingFolder ? 0.6 : 1,
              flexShrink: 0,
            }}
          >
            {pickingFolder ? 'Choosing…' : 'Choose folder'}
          </button>
          <button
            onClick={() => setBannerDismissed(true)}
            title="Dismiss"
            style={{
              padding: '4px',
              background: 'transparent',
              color: 'inherit',
              border: 'none',
              cursor: 'pointer',
              opacity: 0.6,
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      )}

      {/* Media folder status chip — small indicator that the folder is set,
          with click-to-change. Only when set + Tauri (web users don't see). */}
      {!!tauriApi && !!mediaDir && (
        <div style={{
          margin: '8px 16px 0',
          padding: '6px 10px',
          fontSize: '11px',
          color: colors.textMuted,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
            Media folder: {mediaDir}
          </span>
          <button
            onClick={handlePickMediaFolder}
            disabled={pickingFolder}
            style={{
              padding: '2px 6px',
              background: 'transparent',
              color: '#2563EB',
              border: 'none',
              fontSize: '11px',
              cursor: pickingFolder ? 'wait' : 'pointer',
              flexShrink: 0,
            }}
          >
            Change
          </button>
        </div>
      )}

      {/* Search + Page Size Controls */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '0 1 320px', minWidth: '180px' }}>
          <Search size={14} style={{
            position: 'absolute', left: '10px', top: '50%',
            transform: 'translateY(-50%)', color: colors.textMuted, pointerEvents: 'none',
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search across all columns..."
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              fontSize: '13px',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              backgroundColor: isDarkMode ? '#141416' : '#FFFFFF',
              color: colors.text,
              outline: 'none',
            }}
          />
        </div>

        {/* Page size + loading */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: colors.textMuted }}>
          {isLoadingPage && (
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          )}
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{
              padding: '5px 8px',
              fontSize: '12px',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              backgroundColor: isDarkMode ? '#141416' : '#FFFFFF',
              color: colors.text,
              cursor: 'pointer',
            }}
          >
            {[25, 50, 100, 200, 500].map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Sort indicator */}
      {sortConfig.column && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: '12px 16px',
          padding: '8px 12px',
          backgroundColor: isDarkMode ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.1)',
          borderRadius: '6px',
          fontSize: '13px',
          color: isDarkMode ? '#0EA5E9' : '#0284C7',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h6v-2H3v2zM3 6v2h18V6H3zm0 7h12v-2H3v2z"/>
          </svg>
          <span>
            Sorted by <strong>{sortConfig.column}</strong> ({sortConfig.direction === 'asc' ? 'ascending' : 'descending'})
          </span>
          <button
            onClick={() => setSortConfig({ column: null, direction: 'asc' })}
            style={{
              marginLeft: 'auto',
              padding: '2px 8px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: `1px solid ${isDarkMode ? '#0EA5E9' : '#0284C7'}`,
              borderRadius: '4px',
              color: isDarkMode ? '#0EA5E9' : '#0284C7',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            Clear sort
          </button>
        </div>
      )}
      
      {/* Table */}
      <div style={{ 
        maxHeight: isExpanded ? '9999px' : '0px',
        transition: '0.3s ease-in-out',
        overflow: 'hidden',
      }}>
        <div 
          className='data-previewer'
          style={{ 
            overflow: 'auto', 
            maxHeight: '70vh',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            backgroundColor: colors.cardBg,
            position: 'relative',
          }}>
          
          {/* Loading overlay */}
          {isLoadingPage && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: isDarkMode ? 'rgba(13,14,14,0.5)' : 'rgba(255,255,255,0.5)',
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: colors.accent }} />
            </div>
          )}
          
          <table style={{ 
            borderCollapse: 'collapse',
            width: 'max-content',
            minWidth: '100%',
          }}>
            <thead>
              <tr>
                {/* Row number column */}
                <th style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 25,
                  padding: '14px 12px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '11px',
                  color: colors.textMuted,
                  backgroundColor: colors.headerBg,
                  borderBottom: `1px solid ${colors.border}`,
                  borderRight: `1px solid ${colors.border}`,
                  whiteSpace: 'nowrap',
                  width: '60px',
                  minWidth: '60px',
                }}>
                  #
                </th>
                {columns.map((column) => (
                  <th 
                    key={column}
                    onClick={() => handleSort(column)}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 20,
                      padding: '14px 16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      fontSize: '12px',
                      color: sortConfig.column === column ? colors.accent : colors.textSecondary,
                      backgroundColor: colors.headerBg,
                      borderBottom: `1px solid ${colors.border}`,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'color 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {column}
                      <SortIndicator column={column} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} style={{
                    padding: '40px 16px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '14px',
                  }}>
                    {debouncedSearch ? 'No rows match your search.' : 'No data available.'}
                  </td>
                </tr>
              ) : (
                displayRows.map((row, rowIndex) => {
                  const globalRowNum = startRow + rowIndex;
                  return (
                    <tr 
                      key={rowIndex} 
                      className="data-preview-row"
                      style={{
                        backgroundColor: rowIndex % 2 === 0 ? 'transparent' : colors.rowAlt,
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      {/* Row number */}
                      <td style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 10,
                        padding: '12px 12px',
                        fontSize: '11px',
                        fontFamily: '"SF Mono", "Fira Code", monospace',
                        color: colors.textMuted,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        borderRight: `1px solid ${colors.borderLight}`,
                        backgroundColor: rowIndex % 2 === 0 
                          ? (isDarkMode ? '#1a1a1d' : '#FFFFFF') 
                          : colors.rowAlt,
                        textAlign: 'right',
                        width: '60px',
                        minWidth: '60px',
                      }}>
                        {globalRowNum.toLocaleString()}
                      </td>
                      {columns.map((column) => {
                        const cellValue = row[column];
                        const isAudioCell = inlineAudioReady
                          && audioColumns.has(column)
                          && cellValue
                          && AUDIO_EXT_REGEX.test(String(cellValue));
                        // Paired URL value (from a sibling _URL column) so
                        // MediaCell can preview KoBo / SCTO attachments inline.
                        const pairedUrl = urlPairs[column] ? row[urlPairs[column]] : undefined;
                        return (
                          <td
                            key={column}
                            style={{
                              padding: '12px 16px',
                              fontSize: '13px',
                              color: colors.text,
                              borderBottom: `1px solid ${colors.borderLight}`,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: isAudioCell ? '360px' : '300px',
                            }}
                          >
                            {isAudioCell ? (
                              // Tauri + local-file path — existing InlineAudioCell behaviour
                              <InlineAudioCell
                                filename={String(cellValue)}
                                mediaDir={mediaDir}
                                tauriApi={tauriApi}
                                isDarkMode={isDarkMode}
                                onOpenAudit={onNavigate ? () => onNavigate('audio-audit') : undefined}
                              />
                            ) : (
                              // All other cells route through MediaCell, which
                              // handles three modes:
                              //   - Tier 1 media (image/video/pdf) → preview button → lightbox
                              //   - URL but not media → external link
                              //   - Plain text → renders as-is
                              // Audio is intentionally excluded for now (Q3 deferred):
                              // audio cells with paired URLs render as external links rather
                              // than URL-playback widgets, until a backend auth proxy ships.
                              // projectId enables backend media-proxy for KoBo / SCTO
                              // attachments — bypasses Content-Disposition: attachment and
                              // CORS issues that prevent direct in-browser rendering.
                              <MediaCell
                                value={cellValue}
                                urlValue={pairedUrl}
                                projectId={projectId}
                                mediaDir={mediaDir}
                                tauriApi={tauriApi}
                                excludeTypes={['']} // excludeTypes={['audio']}
                                isDarkMode={isDarkMode}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer with pagination */}
        <div style={{
          padding: '12px 16px',
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.headerBg,
          fontSize: '12px',
          color: colors.textSecondary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          borderRadius: '0 0 8px 8px',
          marginTop: '-1px'
        }}>
          {/* Left: row range info */}
          <span>
            {totalRows > 0 
              ? `Rows ${startRow.toLocaleString()}–${endRow.toLocaleString()} of ${totalRows.toLocaleString()}`
              : 'No rows'
            }
            <span style={{ marginLeft: '8px', opacity: 0.7 }}>
              • {activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check'} Data
            </span>
          </span>
          
          {/* Right: pagination controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <PaginationBtn
                onClick={() => setCurrentPage(1)}
                disabled={safePage <= 1 || isLoadingPage}
                isDarkMode={isDarkMode}
                colors={colors}
                title="First page"
              >
                <ChevronsLeft size={14} />
              </PaginationBtn>
              <PaginationBtn
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1 || isLoadingPage}
                isDarkMode={isDarkMode}
                colors={colors}
                title="Previous page"
              >
                <ChevronLeft size={14} />
              </PaginationBtn>

              {/* Page number input for quick jump */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px' }}>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={safePage}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= 1 && val <= totalPages) setCurrentPage(val);
                  }}
                  style={{
                    width: '48px',
                    padding: '4px 6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    textAlign: 'center',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    backgroundColor: isDarkMode ? '#141416' : '#FFFFFF',
                    color: colors.text,
                    outline: 'none',
                  }}
                />
                <span style={{ color: colors.textMuted, fontSize: '12px' }}>
                  of {totalPages.toLocaleString()}
                </span>
              </div>

              <PaginationBtn
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages || isLoadingPage}
                isDarkMode={isDarkMode}
                colors={colors}
                title="Next page"
              >
                <ChevronRight size={14} />
              </PaginationBtn>
              <PaginationBtn
                onClick={() => setCurrentPage(totalPages)}
                disabled={safePage >= totalPages || isLoadingPage}
                isDarkMode={isDarkMode}
                colors={colors}
                title="Last page"
              >
                <ChevronsRight size={14} />
              </PaginationBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// Pagination button helper
const PaginationBtn = ({ onClick, disabled, isDarkMode, colors, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      backgroundColor: isDarkMode ? '#141416' : '#FFFFFF',
      color: disabled ? colors.textMuted : colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s ease',
    }}
  >
    {children}
  </button>
);


export default DataPreview;